import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import styles from './lightbox.module.scss';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import type {LightboxProps} from '@Components/lightbox/lightbox.types';
import {MODAL_DEFAULT_Z_INDEX} from '@Components/modal';

const DEFAULT_Z_INDEX = MODAL_DEFAULT_Z_INDEX - 1;

export function Lightbox({isOpen, onClose, children, zIndex = DEFAULT_Z_INDEX, usePortal = true}: LightboxProps): ReactElement | null {
  const [isVisible, setIsVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return (): void => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const lightboxContent = (
    <>
      <ClickableDiv className={`${styles.lightboxOverlay} ${isVisible ? styles.visible : ''}`} style={{zIndex: zIndex}} onClick={onClose} />
      <ClickableDiv
        className={`${styles.lightboxContent} ${isVisible ? styles.visible : ''}`}
        style={{zIndex: zIndex + 1}}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className={styles.lightboxContentInner}>{children}</div>
      </ClickableDiv>
    </>
  );

  if (usePortal && mounted && typeof document !== 'undefined') {
    return createPortal(lightboxContent, document.body);
  }

  return lightboxContent;
}
